/*
 * Copyright (c) Nexign, JSC, 2021-2022.
 */
/**
 * Account Info page routing
 *
 * @author Yakov Gushchin
 * @author Aleksandr Perkhunov
 * @author Ilya Babanov
 * @version 1.22.0
 * @since 1.11.0
 */

import { memo, useEffect, useState } from 'react';

import { useSelector } from 'react-redux';
import { Redirect, Route, RouteComponentProps, Switch } from 'react-router';

import { EBreadCrumbType } from '@modules/breadCrumbs/breadCrumbsTypes';
import { AccessComponentKeyEnum } from '@modules/role/constants/guard/guardConstants';

import { CLIENT_ID, RoutePath } from '../../routes/constants/routesConstants';
import PrivateRoute from '../../routes/PrivateRoute';
import { PageNotFound } from '../ErrorPage/PageNotFound';
import AccountInfoDelivery from './components/AccountInfoDelivery/AccountInfoDelivery';
import AccountInfoFinancialDiscounts from './components/AccountInfoFinancialDiscounts';
import AccountInfoFinancialGeneral from './components/AccountInfoFinancialGeneral';
import AccountInfoFinancialMultiBalances from './components/AccountInfoFinancialMultiBalances';
import AccountInfoGeneral from './components/AccountInfoGeneral';
import PageAccountInfo from './components/PageAccountInfo';
import { makeSelectAccountInfoGeneralMetaIsSingle } from './pageAccountInfoSelectors';


const PageAccountInfoRoute = (props: RouteComponentProps<{ clientId: string }>) => {
    const clientId = props.match.params.clientId;

    const [breadcrumbs, setBreadCrumbs] = useState<EBreadCrumbType[]>();

    const isSingle = useSelector(makeSelectAccountInfoGeneralMetaIsSingle(clientId));

    useEffect(() => {
        setBreadCrumbs(!isSingle ? [EBreadCrumbType.accounts] : []);
    }, [isSingle]);


    return <PageAccountInfo>
        <Switch>
            {/* Generel tab */}
            <PrivateRoute
                exact
                accessKey={AccessComponentKeyEnum.PageAccountInfoGeneral}
                breadCrumbs={breadcrumbs}
                component={AccountInfoGeneral}
                path={RoutePath.accountInfoGeneral}
            />
            {/*/!* Finances tabs *!/*/}
            <PrivateRoute
                exact
                accessKey={AccessComponentKeyEnum.PageAccountInfoFinances}
                breadCrumbs={breadcrumbs}
                component={AccountInfoFinancialGeneral}
                path={RoutePath.accountInfoFinancialGeneral}
            />
            {/* Finance Tab - Discounts */}
            <PrivateRoute
                exact
                accessKey={AccessComponentKeyEnum.PageAccountInfoFinances}
                breadCrumbs={breadcrumbs}
                component={AccountInfoFinancialDiscounts}
                path={RoutePath.accountInfoFinancialDiscounts}
            />
            {/* Finance Tab - Multi balances */}
            <PrivateRoute
                exact
                accessKey={AccessComponentKeyEnum.PageAccountInfoFinances}
                breadCrumbs={breadcrumbs}
                component={AccountInfoFinancialMultiBalances}
                path={RoutePath.accountInfoFinancialMultiBalances}
            />
            {/* Shipping accs Tab */}
            <PrivateRoute
                exact
                accessKey={AccessComponentKeyEnum.PageAccountInfoShipping}
                breadCrumbs={breadcrumbs}
                component={AccountInfoDelivery}
                path={RoutePath.accountInfoAccs}
            />
            <Route exact path={RoutePath.accountInfoFinancial}>
                <Redirect to={RoutePath.accountInfoFinancialGeneral.replace(CLIENT_ID, clientId)}/>
            </Route>
            <Route exact path={RoutePath.accountInfo}>
                <Redirect to={RoutePath.accountInfoGeneral.replace(CLIENT_ID, clientId)}/>
            </Route>

            <PrivateRoute
                accessKey={AccessComponentKeyEnum.PageNotFound}
                component={PageNotFound}
                path="**"
            />

        </Switch>
    </PageAccountInfo>;
};

export default memo(PageAccountInfoRoute);
